import { useState, useEffect, type ReactNode } from 'react'
import { ThemeContext, type Theme } from '@/hooks/useTheme'

interface ThemeProviderProps {
  children: ReactNode
  defaultTheme?: Theme
}

const isThemeValue = (value: string | null): value is Theme =>
  value === 'light' || value === 'dark' || value === 'system'

const getSystemTheme = (): 'light' | 'dark' =>
  window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

export const ThemeProvider = ({ 
  children, 
  defaultTheme = 'dark' 
}: ThemeProviderProps) => {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem('theme')
    return isThemeValue(stored) ? stored : defaultTheme
  })

  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => {
    return theme === 'system' ? getSystemTheme() : theme
  })

  useEffect(() => {
    // Save theme preference to localStorage
    localStorage.setItem('theme', theme)

    // Update resolved theme and apply to DOM
    let newResolvedTheme: 'light' | 'dark'
    
    newResolvedTheme = theme === 'system' ? getSystemTheme() : theme

    setResolvedTheme(newResolvedTheme)

    // Apply theme to document
    const root = document.documentElement
    root.classList.remove('light', 'dark')
    root.classList.add(newResolvedTheme)
    root.setAttribute('data-theme', newResolvedTheme)
  }, [theme])

  useEffect(() => {
    // Listen for system theme changes when using system theme
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      
      const handleChange = (e: MediaQueryListEvent) => {
        const newResolvedTheme = e.matches ? 'dark' : 'light'
        setResolvedTheme(newResolvedTheme)
        
        // Apply to DOM
        const root = document.documentElement
        root.classList.remove('light', 'dark')  
        root.classList.add(newResolvedTheme)
        root.setAttribute('data-theme', newResolvedTheme)
      }

      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
  }, [theme])

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}
